import { Translation, useIsMounted } from '@ps/web-shared-components';
import type { SelectOption } from '@ps/web-shared-components';
import { required } from '@ps/web-shared-validators';
import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Country } from '@swf/core/enums';
import { select } from '@swf/core/store';

import { State } from '../../../../../../types/frenchState';
import { getCountryStates } from '../../../../../../utils/country-states/country-states';
import {
  countriesWithoutFrenchTerritories as countries,
  frenchTerritories,
  getFrenchCitiesByState
} from '../../../../../../utils/placeOfBirth';
import City from '../../../../../City/City';
import { findState } from '../../../../../State/State';
import RMFDropDown from '../../../RMFDropDown/RMFDropDown';

import {
  TXTACL_Please_Select,
  TXTCLI_City_of_Birth,
  TXTCLI_Country_of_Birth,
  TXTCLI_Please_select_your_department__ELL,
  TXTWCL_Department_of_birth,
  TXTWCL_Please_enter_a_valid_cityX,
  TXTWCL_Please_select_your_city_of_bir_ELL
} from './PlaceOfBirthFieldsFR.messages.json';
import { city, wrapper } from './PlaceOfBirthFieldsFR.scss';

const frenchStatesWithNoCities = [
  State.AntarcticTerritories,
  State.ClippertonIsland,
  State.FrenchPolynesia,
  State.NewCaledonia,
  State.SaintBarthelemy,
  State.SaintMartin,
  State.SaintPierre,
  State.WallisAndFutuna
];

const countryOfBirthField = 'countryOfBirth';
const stateOfBirthField = 'stateOfBirth';
const cityOfBirthField = 'cityOfBirth';

const PlaceOfBirthFieldsFR = () => {
  const form = useForm();
  const country = useSelector(select.account.getCountry) as Country;
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);
  const initialCountry = frenchTerritories.includes(country) ? Country.France : country;
  const isMounted = useIsMounted();

  const {
    input: { value: countryOfBirth }
  } = useField(countryOfBirthField, { initialValue: initialCountry });
  const {
    input: { value: stateOfBirth }
  } = useField(stateOfBirthField);

  const countryFrance = countryOfBirth === Country.France;

  const stateOptions = (
    getCountryStates(countryOfBirth) as { value: State; text: string }[]
  ).filter((state) => !frenchStatesWithNoCities.includes(state?.value));

  const resetCityState = () => {
    form.change(cityOfBirthField);
    form.resetFieldState(cityOfBirthField);
  };

  const setFrenchCities = useCallback(async () => {
    const frenchCitiesByState = await getFrenchCitiesByState(
      findState(stateOfBirth, stateOptions)?.value ?? ''
    );
    isMounted() && setCityOptions(frenchCitiesByState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOfBirth]);

  useEffect(() => {
    isMounted() && setFrenchCities();
  }, [setFrenchCities, isMounted]);

  return (
    <div className={wrapper}>
      <RMFDropDown
        id={countryOfBirthField}
        label={TXTCLI_Country_of_Birth}
        options={countries}
        onSelect={resetCityState}
      />
      {countryFrance && (
        <RMFDropDown
          id={stateOfBirthField}
          label={TXTWCL_Department_of_birth}
          options={stateOptions}
          onSelect={resetCityState}
          validators={[required(TXTCLI_Please_select_your_department__ELL)]}
          placeholder={<Translation {...TXTACL_Please_Select} />}
        />
      )}
      {countryFrance ? (
        <RMFDropDown
          id={cityOfBirthField}
          label={TXTCLI_City_of_Birth}
          options={cityOptions}
          validators={[required(TXTWCL_Please_select_your_city_of_bir_ELL)]}
          placeholder={<Translation {...TXTACL_Please_Select} />}
          disabled={!cityOptions?.length}
          showPlaceholder
        />
      ) : (
        <City
          fieldName={cityOfBirthField}
          requiredMessage={TXTWCL_Please_enter_a_valid_cityX}
          label={TXTCLI_City_of_Birth}
          className={city}
        />
      )}
    </div>
  );
};

export default PlaceOfBirthFieldsFR;
